import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Container, Grid,
  IconButton, InputAdornment, Alert, CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { getCurrentLanguage } from '../../utils/navigation';
import { useTranslation } from 'react-i18next';
import AuthNavbar from '../layout/AuthNavbar';

const RegisterForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', firstName: '', lastName: '', phoneNumber: '' });
  const { t } = useTranslation();
  const { register } = useAuth();

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName || !formData.phoneNumber) {
      setError(t('auth.errors.allFieldsRequired')); return false;
    }
    if (formData.password.length < 6) { setError(t('auth.errors.passwordLength')); return false; }
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.phoneNumber)) { setError(t('auth.errors.invalidPhone')); return false; }
    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;
    setLoading(true);
    try {
      const firstName = formData.firstName.trim() || 'User';
      const lastName = formData.lastName.trim() || '';
      const displayName = firstName + (lastName ? ` ${lastName}` : '');
      const response = await register({
        email: formData.email, password: formData.password,
        displayName, firstName, lastName, phoneNumber: formData.phoneNumber
      });
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify({ ...response.user, firstName, lastName, displayName, phoneNumber: formData.phoneNumber }));
      navigate(`/${getCurrentLanguage()}/auth/role-selection`);
    } catch (error) {
      setError(error.message || t('auth.errors.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AuthNavbar />
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#FAFAFA', pt: 8, pb: 4 }}>
        <Container maxWidth="sm">
          <Box sx={{
            bgcolor: '#fff', borderRadius: 3,
            boxShadow: '0 2px 20px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0',
            p: { xs: 3, sm: 5 }, mx: 'auto', maxWidth: 480
          }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box component="img" src="/images/lingoply-logo.jpg" alt="Lingoply"
                sx={{ width: 64, height: 64, borderRadius: '50%', mb: 1.5 }} />
              <Typography variant="h5" fontWeight={700} color="#1a1a1a">{t('auth.createAccount')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t('auth.createAccountDesc')}</Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={6}>
                <TextField required fullWidth label={t('auth.firstName')} name="firstName" value={formData.firstName} onChange={handleChange} />
              </Grid>
              <Grid item xs={6}>
                <TextField required fullWidth label={t('auth.lastName')} name="lastName" value={formData.lastName} onChange={handleChange} />
              </Grid>
            </Grid>

            <TextField fullWidth required label={t('auth.emailAddress')} name="email" type="email"
              value={formData.email} onChange={handleChange} sx={{ mb: 2, mt: 1 }} />

            <TextField fullWidth required label={t('auth.phoneNumber')} name="phoneNumber" type="tel"
              value={formData.phoneNumber} onChange={handleChange} placeholder="0612345678" sx={{ mb: 2 }} />

            <TextField fullWidth required name="password" label={t('auth.password')}
              type={showPassword ? 'text' : 'password'} value={formData.password}
              onChange={handleChange} sx={{ mb: 3 }}
              InputProps={{ endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )}}
            />

            <Button fullWidth variant="contained" onClick={handleRegister} disabled={loading}
              sx={{ py: 1.5, mb: 2, fontSize: '1rem', fontWeight: 600, borderRadius: 2.5,
                bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' } }}>
              {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : t('auth.createAccount')}
            </Button>

            <Typography variant="body2" textAlign="center" color="text.secondary">
              {t('auth.haveAccount')}{' '}
              <Link to={`/${getCurrentLanguage()}/auth/login`}
                style={{ textDecoration: 'none', color: '#F5A623', fontWeight: 600 }}>
                {t('auth.signIn')}
              </Link>
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default RegisterForm;
